import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router";
import { Search, SlidersHorizontal, ChevronDown, RefreshCw, Calendar, Users, Coins } from "lucide-react";
import { useAuthStore } from "@/shared/hooks/use-auth-store";
import {
  useHallBountiesV2,
  usePublishedBounties,
  useParticipatedBounties,
  formatReward,
  formatDate,
  getDaysRemaining,
  getStatusConfig,
  getBidStatusConfig,
  useSubmitBidV2,
} from "@/lib/hooks/use-bounties-v2";
import type { HallBountyItem, PublishedBountyItem, ParticipatedBountyItem } from "@/lib/hooks/use-bounties-v2";
import type { BountyStatus } from "@/lib/supabase/types";
import { BidModal } from "./bid-modal";
import { getStatusLabel } from "@/domains/bounties/status-machine";
import styles from "./tasks-page.module.css";

// ========== Constants ==========
const CATEGORIES = ["全部", "前端开发", "后端开发", "移动开发", "AI/ML", "数据分析", "产品设计", "测试", "其他"];
const PAGE_SIZE = 10;

type TabType = "hall" | "published" | "participated";
type SortType = "default" | "reward" | "deadline";

// ========== Helper Components ==========

function StatusBadge({ status }: { status: BountyStatus }) {
  const config = getStatusConfig(status);
  return (
    <span
      className={styles.statusBadge}
      style={{ color: config.color, backgroundColor: config.bgColor }}
    >
      {config.label}
    </span>
  );
}

function DeadlineDisplay({ date, prefix = "截止" }: { date: string | null; prefix?: string }) {
  const days = getDaysRemaining(date);
  if (days === null) return <span className={styles.deadline}>未设置</span>;

  const isUrgent = days !== null && days <= 3 && days >= 0;
  const isOverdue = days !== null && days < 0;

  return (
    <span className={`${styles.deadline} ${isUrgent ? styles.urgent : ""} ${isOverdue ? styles.overdue : ""}`}>
      <Calendar size={12} />
      {isOverdue ? "已截止" : `${prefix} ${formatDate(date)}`}
      {!isOverdue && days !== null && <span className={styles.daysLeft}>({days}天)</span>}
    </span>
  );
}

// ========== Skeleton Components ==========

function SkeletonCard() {
  return (
    <div className={styles.skeletonCard}>
      <div className={styles.skeletonHeader}>
        <div className={styles.skeletonTitle} />
        <div className={styles.skeletonAmount} />
      </div>
      <div className={styles.skeletonMeta}>
        <div className={styles.skeletonTag} />
        <div className={styles.skeletonTag} style={{ width: "80px" }} />
      </div>
      <div className={styles.skeletonTags}>
        <div className={styles.skeletonTag} style={{ width: "60px" }} />
        <div className={styles.skeletonTag} style={{ width: "50px" }} />
      </div>
    </div>
  );
}

// ========== Empty State ==========

function EmptyState({ type }: { type: TabType }) {
  const messages: Record<TabType, { title: string; desc: string }> = {
    hall: {
      title: "暂无相关任务",
      desc: "试试其他筛选条件，或查看其他分类",
    },
    published: {
      title: "您还没有发布任务",
      desc: "发布您的第一个任务，开启赏金之旅",
    },
    participated: {
      title: "暂无参与记录",
      desc: "去任务大厅投递方案，开始赚钱吧",
    },
  };

  const msg = messages[type];

  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <svg viewBox="0 0 120 120" fill="none">
          <circle cx="60" cy="60" r="50" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" opacity="0.3" />
          <path d="M45 55c0-8.284 6.716-15 15-15s15 6.716 15 15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <circle cx="45" cy="55" r="4" fill="currentColor" />
          <circle cx="75" cy="55" r="4" fill="currentColor" />
          <path d="M50 75c5-5 15-5 20 0" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
      <div className={styles.emptyTitle}>{msg.title}</div>
      <div className={styles.emptyDesc}>{msg.desc}</div>
      {type !== "hall" && (
        <Link to="/tasks" className={styles.emptyAction}>
          去任务大厅
        </Link>
      )}
    </div>
  );
}

// ========== Task Cards ==========

interface HallTaskCardProps {
  task: HallBountyItem;
  onClick: () => void;
  onBid: (e: React.MouseEvent) => void;
}

function HallTaskCard({ task, onClick, onBid }: HallTaskCardProps) {
  return (
    <div className={styles.taskCard} onClick={onClick}>
      <div className={styles.taskHeader}>
        <h3 className={styles.taskTitle}>{task.title}</h3>
        <span className={styles.amount}>{formatReward(task.reward_usdc)}</span>
      </div>

      <div className={styles.taskMeta}>
        <span className={styles.bidCount}>
          <Users size={12} />
          {task.bid_count} 人投递
        </span>
        <DeadlineDisplay date={task.bidding_deadline} prefix="投递截止" />
      </div>

      <div className={styles.taskTags}>
        {task.category && <span className={styles.categoryTag}>{task.category}</span>}
        {task.tech_tags?.slice(0, 3).map((tag) => (
          <span key={tag} className={styles.techTag}>{tag}</span>
        ))}
      </div>

      <StatusBadge status={task.status} />

      <button className={styles.bidBtn} onClick={onBid}>
        投递
      </button>
    </div>
  );
}

interface PublishedTaskCardProps {
  task: PublishedBountyItem;
  onClick: () => void;
}

function PublishedTaskCard({ task, onClick }: PublishedTaskCardProps) {
  // 根据不同状态显示不同信息
  const getActionInfo = () => {
    switch (task.status) {
      case "bidding":
        return { label: "选标", count: `${task.bid_count} 份方案` };
      case "in_progress":
        return { label: "查看", count: "开发中" };
      case "reviewing":
        return { label: "审查", count: `第${task.review_round}轮` };
      case "completed":
        return { label: "查看", count: "已完成" };
      default:
        return { label: "查看", count: "" };
    }
  };

  const action = getActionInfo();

  return (
    <div className={styles.taskCard} onClick={onClick}>
      <div className={styles.taskHeader}>
        <h3 className={styles.taskTitle}>{task.title}</h3>
      </div>

      <div className={styles.taskMeta}>
        <StatusBadge status={task.status} />
        <span className={styles.actionCount}>
          {action.count}
        </span>
      </div>

      <div className={styles.taskInfoRow}>
        {task.status === "bidding" && (
          <DeadlineDisplay date={task.bidding_deadline} prefix="投递截止" />
        )}
        {(task.status === "in_progress" || task.status === "reviewing") && (
          <DeadlineDisplay date={task.completion_deadline} prefix="项目截止" />
        )}
        <span className={styles.reward}>{formatReward(task.reward_usdc)}</span>
      </div>

      <button className={styles.viewBtn}>
        {action.label}
      </button>
    </div>
  );
}

interface ParticipatedTaskCardProps {
  task: ParticipatedBountyItem;
  onClick: () => void;
}

function ParticipatedTaskCard({ task, onClick }: ParticipatedTaskCardProps) {
  const bidConfig = getBidStatusConfig(task.bid_status);

  return (
    <div className={styles.taskCard} onClick={onClick}>
      <div className={styles.taskHeader}>
        <h3 className={styles.taskTitle}>{task.title}</h3>
        <span
          className={styles.bidStatus}
          style={{ color: bidConfig.color }}
        >
          {bidConfig.label}
        </span>
      </div>

      <div className={styles.taskMeta}>
        <StatusBadge status={task.bounty_status} />
        {task.bounty_status === "bidding" && (
          <DeadlineDisplay date={task.bidding_deadline} prefix="投递截止" />
        )}
        {(task.bounty_status === "in_progress" || task.bounty_status === "reviewing") && (
          <DeadlineDisplay date={task.completion_deadline} prefix="项目截止" />
        )}
      </div>

      <div className={styles.taskInfoRow}>
        {task.bounty_status === "reviewing" && (
          <span className={styles.reviewRound}>第 {task.review_round} 轮审查</span>
        )}
        <span className={styles.reward}>{formatReward(task.reward_usdc)}</span>
      </div>
    </div>
  );
}

// ========== Main Component ==========

export default function TasksPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Tab & filters state
  const [activeTab, setActiveTab] = useState<TabType>("hall");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("全部");
  const [sortType, setSortType] = useState<SortType>("default");
  const [showSortMenu, setShowSortMenu] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [accumulatedItems, setAccumulatedItems] = useState<(
    | HallBountyItem
    | PublishedBountyItem
    | ParticipatedBountyItem
  )[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Bid modal state
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<HallBountyItem | null>(null);
  const [bidError, setBidError] = useState<string | null>(null);

  // Queries
  const hallQuery = useHallBountiesV2({
    category: activeCategory,
    search: searchQuery,
    sort: sortType,
    page,
    pageSize: PAGE_SIZE,
  });

  const publishedQuery = usePublishedBounties({
    publisherId: user?.id ?? null,
    page,
    pageSize: PAGE_SIZE,
  });

  const participatedQuery = useParticipatedBounties({
    engineerId: user?.id ?? null,
    page,
    pageSize: PAGE_SIZE,
  });

  // Active query based on tab
  const activeQuery =
    activeTab === "hall"
      ? hallQuery
      : activeTab === "published"
      ? publishedQuery
      : participatedQuery;

  // Submit bid mutation
  const submitBidMutation = useSubmitBidV2();

  // Reset on tab/filter change
  const resetPagination = useCallback(() => {
    setPage(0);
    setAccumulatedItems([]);
  }, []);

  // Handle tab change
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    resetPagination();
  };

  // Handle filter change
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    resetPagination();
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    resetPagination();
  };

  const handleSortChange = (sort: SortType) => {
    setSortType(sort);
    setShowSortMenu(false);
    resetPagination();
  };

  // Load more
  const handleLoadMore = () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    setPage((p) => p + 1);
  };

  // Refresh
  const handleRefresh = () => {
    resetPagination();
    activeQuery.refetch();
  };

  // Navigation
  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  // Bid handlers
  const handleBidClick = (e: React.MouseEvent, task: HallBountyItem) => {
    e.stopPropagation();
    if (!user) {
      navigate("/login");
      return;
    }
    setSelectedTask(task);
    setBidError(null);
    setBidModalOpen(true);
  };

  const handleBidSubmit = async (data: { proposal: string; estimatedDays: number; contactInfo: string; proposedReward: number }) => {
    if (!selectedTask) return;

    try {
      await submitBidMutation.mutateAsync({
        bountyId: selectedTask.id,
        ...data,
      });
      setBidModalOpen(false);
      setBidError(null);
      setSelectedTask(null);
      // Navigate to detail page after successful submission
      navigate(`/tasks/${selectedTask.id}`);
    } catch (error) {
      setBidError(error instanceof Error ? error.message : '投递失败，请重试');
    }
  };

  // Derived state
  const isLoading = activeQuery.isLoading && accumulatedItems.length === 0;
  const isRefreshing = activeQuery.isFetching && !activeQuery.isLoading && !isLoadingMore;
  const total = activeQuery.data?.total ?? 0;
  const hasMore = accumulatedItems.length < total;

  // Sync accumulated items with query data
  useEffect(() => {
    if (activeQuery.data?.items && !activeQuery.isFetching) {
      const items = activeQuery.data.items;
      if (page === 0) {
        setAccumulatedItems(items);
      } else {
        setAccumulatedItems((prev) => {
          const existingIds = new Set(prev.map((i) => (i as { id: string }).id));
          const newItems = items.filter((i) => !existingIds.has(i.id));
          return [...prev, ...newItems];
        });
      }
      setIsLoadingMore(false);
    }
  }, [activeQuery.data?.items, activeQuery.isFetching, page]);

  // Render task list based on tab
  const renderTaskList = () => {
    if (isLoading) {
      return (
        <div className={styles.taskList}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      );
    }

    if (accumulatedItems.length === 0) {
      return <EmptyState type={activeTab} />;
    }

    return (
      <div className={styles.taskList}>
        {activeTab === "hall" &&
          (accumulatedItems as HallBountyItem[]).map((task) => (
            <HallTaskCard
              key={task.id}
              task={task}
              onClick={() => handleTaskClick(task.id)}
              onBid={(e) => handleBidClick(e, task)}
            />
          ))}

        {activeTab === "published" &&
          (accumulatedItems as PublishedBountyItem[]).map((task) => (
            <PublishedTaskCard
              key={task.id}
              task={task}
              onClick={() => handleTaskClick(task.id)}
            />
          ))}

        {activeTab === "participated" &&
          (accumulatedItems as ParticipatedBountyItem[]).map((task) => (
            <ParticipatedTaskCard
              key={task.id}
              task={task}
              onClick={() => handleTaskClick(task.id)}
            />
          ))}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <h1>任务中心</h1>
        <Link to="/tasks/create" className={styles.createBtn}>
          发布任务
        </Link>
      </header>

      {/* Tabs */}
      <div className={styles.tabs}>
        {[
          { key: "hall", label: "任务大厅" },
          { key: "published", label: "我发布的" },
          { key: "participated", label: "我参与的" },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ""}`}
            onClick={() => handleTabChange(tab.key as TabType)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search Bar (Task Hall only) */}
      {activeTab === "hall" && (
        <div className={styles.searchBar}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="搜索任务..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              className={styles.clearBtn}
              onClick={() => handleSearchChange("")}
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      {activeTab === "hall" && (
        <div className={styles.filterBar}>
          <div className={styles.categories}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`${styles.categoryChip} ${activeCategory === cat ? styles.activeCategory : ""}`}
                onClick={() => handleCategoryChange(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className={styles.sortWrapper}>
            <button
              className={styles.sortBtn}
              onClick={() => setShowSortMenu(!showSortMenu)}
            >
              <SlidersHorizontal size={16} />
              <span>排序</span>
              <ChevronDown
                size={14}
                className={showSortMenu ? styles.rotated : ""}
              />
            </button>
            {showSortMenu && (
              <div className={styles.sortMenu}>
                {[
                  { value: "default", label: "默认排序" },
                  { value: "reward", label: "按赏金" },
                  { value: "deadline", label: "按截止时间" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    className={`${styles.sortOption} ${sortType === opt.value ? styles.activeSort : ""}`}
                    onClick={() => handleSortChange(opt.value as SortType)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Refresh Indicator */}
      {isRefreshing && (
        <div className={styles.refreshIndicator}>
          <RefreshCw size={18} className={styles.refreshIcon} />
          <span>刷新中...</span>
        </div>
      )}

      {/* Error State */}
      {activeQuery.error && (
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>加载失败</div>
          <div className={styles.emptyDesc}>{(activeQuery.error as Error).message}</div>
          <button className={styles.emptyAction} onClick={handleRefresh}>
            点击重试
          </button>
        </div>
      )}

      {/* Content */}
      {!activeQuery.error && (
        <div className={styles.content}>
          {renderTaskList()}

          {/* Load More */}
          {!isLoading && accumulatedItems.length > 0 && hasMore && (
            <button
              className={styles.loadMoreBtn}
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <span className={styles.spinner} />
                  <span>加载中...</span>
                </>
              ) : (
                <>
                  <span>加载更多</span>
                </>
              )}
            </button>
          )}

          {!isLoading && accumulatedItems.length > 0 && !hasMore && (
            <div className={styles.noMore}>— 没有更多了 —</div>
          )}
        </div>
      )}

      {/* Bid Modal */}
      {bidModalOpen && selectedTask && (
        <BidModal
          bountyId={selectedTask.id}
          bountyTitle={selectedTask.title}
          rewardUsdc={selectedTask.reward_usdc}
          onClose={() => {
            setBidModalOpen(false);
            setBidError(null);
            setSelectedTask(null);
          }}
          onSubmit={handleBidSubmit}
          isSubmitting={submitBidMutation.isPending}
          error={bidError}
        />
      )}
    </div>
  );
}
